import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Check, ChevronRight, Calendar } from "lucide-react";

// Screen 1: Nia Listening
const NiaListeningScreen = ({ isActive }) => {
  const [displayedWords, setDisplayedWords] = useState([]);
  const [showThinking, setShowThinking] = useState(false);
  const [thinkingGlow, setThinkingGlow] = useState(false);
  
  const userText = "I know I want to do something new… maybe in healthcare… but I'm just not sure…";
  const words = userText.split(" ");
  
  useEffect(() => {
    if (!isActive) {
      setDisplayedWords([]);
      setShowThinking(false);
      setThinkingGlow(false);
      return;
    }
    
    let wordIndex = 0;
    let timeoutId;
    
    setDisplayedWords([]);
    setShowThinking(false);
    setThinkingGlow(false);
    
    const typeWord = () => {
      if (wordIndex < words.length) {
        setDisplayedWords(prev => [...prev, words[wordIndex]]);
        wordIndex++;
        timeoutId = setTimeout(typeWord, 180);
      } else {
        timeoutId = setTimeout(() => {
          setThinkingGlow(true);
          setShowThinking(true);
        }, 400);
      }
    };
    
    timeoutId = setTimeout(typeWord, 300);
    
    return () => clearTimeout(timeoutId);
  }, [isActive]);
  
  return (
    <div className="flex flex-col items-center pt-6 px-4 h-full">
      {/* Listening Indicator */}
      <div className="relative mt-4 mb-6">
        {/* Outer pulsing rings */}
        <motion.div
          animate={{
            scale: [1, 1.4, 1],
            opacity: [0.3, 0, 0.3],
          }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute rounded-full"
          style={{ 
            width: 90, height: 90, left: -21, top: -21,
            background: thinkingGlow 
              ? 'radial-gradient(circle, rgba(245,200,130,0.3) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(196,181,212,0.3) 0%, rgba(232,180,188,0.2) 50%, transparent 70%)'
          }}
        />
        <motion.div
          animate={{
            scale: [1, 1.25, 1],
            opacity: [0.5, 0.1, 0.5],
          }}
          transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
          className="absolute rounded-full"
          style={{ 
            width: 70, height: 70, left: -11, top: -11,
            background: thinkingGlow 
              ? 'radial-gradient(circle, rgba(245,200,130,0.4) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(196,181,212,0.4) 0%, rgba(232,180,188,0.3) 50%, transparent 70%)'
          }}
        />
        
        {/* Main circle */}
        <motion.div
          animate={{
            boxShadow: thinkingGlow
              ? ['0 0 20px rgba(245,200,130,0.4)', '0 0 35px rgba(245,200,130,0.6)', '0 0 20px rgba(245,200,130,0.4)']
              : ['0 0 15px rgba(196,181,212,0.3)', '0 0 25px rgba(232,180,188,0.4)', '0 0 15px rgba(196,181,212,0.3)']
          }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-12 h-12 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/20"
          style={{
            background: thinkingGlow
              ? 'linear-gradient(135deg, rgba(245,200,130,0.5) 0%, rgba(245,168,130,0.4) 100%)'
              : 'linear-gradient(135deg, rgba(232,180,188,0.4) 0%, rgba(196,181,212,0.4) 100%)'
          }}
        >
          <motion.div
            animate={{ scale: [1, 1.15, 1] }}
            transition={{ duration: 0.8, repeat: Infinity }}
          >
            <Mic className="w-5 h-5 text-white/90" />
          </motion.div>
        </motion.div>
      </div>
      
      {/* User Text Area */}
      <div className="px-2 min-h-[80px] text-center">
        <AnimatePresence mode="wait">
          {!showThinking ? (
            <motion.p
              key="userText"
              className="text-white/90 text-sm leading-relaxed font-light"
            >
              {displayedWords.map((word, index) => (
                <motion.span
                  key={index}
                  initial={{ opacity: 0, y: 3 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15 }}
                  className="inline-block mr-1"
                >
                  {word}
                </motion.span>
              ))}
              {displayedWords.length > 0 && displayedWords.length < words.length && (
                <motion.span
                  animate={{ opacity: [1, 0, 1] }}
                  transition={{ duration: 0.6, repeat: Infinity }}
                  className="inline-block w-0.5 h-3 bg-white/60 ml-0.5 align-middle"
                />
              )}
            </motion.p>
          ) : (
            <motion.p
              key="thinking"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-amber-200/80 text-sm italic"
            >
              Nia is thinking
              <motion.span
                animate={{ opacity: [0, 1, 0] }}
                transition={{ duration: 1.2, repeat: Infinity }}
              >
                ...
              </motion.span>
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// Screen 2: Emotional Readiness Dashboard
const EmotionalReadinessScreen = ({ isActive }) => {
  const [gaugeValue, setGaugeValue] = useState(0);
  const [showBars, setShowBars] = useState([false, false, false, false]);
  const [showMessage, setShowMessage] = useState(false);
  
  const progressBars = [
    { label: "Psychosocial Foundation", value: 72 },
    { label: "Cognitive Coherence", value: 65 },
    { label: "Emotional Stability", value: 61 },
    { label: "Reflection Depth", value: 70 },
  ];
  
  useEffect(() => {
    if (!isActive) {
      setGaugeValue(0);
      setShowBars([false, false, false, false]);
      setShowMessage(false);
      return;
    }
    
    // Animate gauge
    const gaugeInterval = setInterval(() => {
      setGaugeValue(prev => {
        if (prev >= 68) {
          clearInterval(gaugeInterval);
          return 68;
        }
        return prev + 2;
      });
    }, 30);
    
    // Show bars sequentially
    const barTimeouts = progressBars.map((_, index) => 
      setTimeout(() => {
        setShowBars(prev => {
          const newBars = [...prev];
          newBars[index] = true;
          return newBars;
        });
      }, 600 + index * 350)
    );
    
    // Show message
    const messageTimeout = setTimeout(() => setShowMessage(true), 2200);
    
    return () => {
      clearInterval(gaugeInterval);
      barTimeouts.forEach(t => clearTimeout(t));
      clearTimeout(messageTimeout);
    };
  }, [isActive]);
  
  return (
    <div className="flex flex-col items-center pt-4 px-4 h-full">
      {/* Circular Gauge */}
      <div className="relative w-24 h-24 mb-4">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          {/* Background circle */}
          <circle
            cx="50" cy="50" r="42"
            fill="none"
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="8"
          />
          {/* Progress circle */}
          <motion.circle
            cx="50" cy="50" r="42"
            fill="none"
            stroke="url(#gaugeGradient)"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${(gaugeValue / 100) * 264} 264`}
            initial={{ strokeDasharray: "0 264" }}
          />
          <defs>
            <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#f5a882" />
              <stop offset="100%" stopColor="#c4b5d4" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold text-white">{gaugeValue}</span>
          <span className="text-[10px] text-white/50">/100</span>
        </div>
      </div>
      
      <p className="text-white/80 text-xs mb-4">Emotional Readiness: <span className="text-amber-200">Near Ready</span></p>
      
      {/* Progress Bars */}
      <div className="w-full space-y-2.5 px-2">
        {progressBars.map((bar, index) => (
          <motion.div
            key={bar.label}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: showBars[index] ? 1 : 0, x: showBars[index] ? 0 : -10 }}
            transition={{ duration: 0.3 }}
            className="flex items-center gap-2"
          >
            <div className="w-4 h-4 rounded-full bg-green-500/20 flex items-center justify-center">
              <Check className="w-2.5 h-2.5 text-green-400" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between text-[10px] mb-1">
                <span className="text-white/70">{bar.label}</span>
                <span className="text-white/50">{bar.value}</span>
              </div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: showBars[index] ? `${bar.value}%` : 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="h-full rounded-full"
                  style={{ background: 'linear-gradient(90deg, #f5a882, #c4b5d4)' }}
                />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      
      {/* Status Message */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: showMessage ? 1 : 0 }}
        className="text-white/60 text-[10px] italic text-center mt-4 px-2"
      >
        Your thinking is clear. Keep journaling to build momentum.
      </motion.p>
    </div>
  );
};

// Screen 3: Your Path Forward
const PathForwardScreen = ({ isActive }) => {
  const [showItems, setShowItems] = useState([false, false, false]);
  const [showCalendar, setShowCalendar] = useState(false);
  
  const milestones = [
    { label: "Completed first journal entry", completed: true },
    { label: "Career values assessed", completed: true },
    { label: "Explore healthcare pathways", completed: false, active: true },
  ];
  
  useEffect(() => {
    if (!isActive) {
      setShowItems([false, false, false]);
      setShowCalendar(false);
      return;
    }
    
    const itemTimeouts = milestones.map((_, index) => 
      setTimeout(() => {
        setShowItems(prev => {
          const newItems = [...prev];
          newItems[index] = true;
          return newItems;
        });
      }, 300 + index * 500)
    );
    
    const calendarTimeout = setTimeout(() => setShowCalendar(true), 2000);
    
    return () => {
      itemTimeouts.forEach(t => clearTimeout(t));
      clearTimeout(calendarTimeout);
    };
  }, [isActive]);
  
  return (
    <div className="flex flex-col items-center pt-6 px-4 h-full">
      <h3 className="text-white/90 text-sm font-medium mb-6">Your Path Forward</h3>
      
      {/* Milestone List */}
      <div className="w-full space-y-4 px-2">
        {milestones.map((item, index) => (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, x: -15 }}
            animate={{ opacity: showItems[index] ? 1 : 0, x: showItems[index] ? 0 : -15 }}
            transition={{ duration: 0.4 }}
            className={`flex items-center gap-3 p-3 rounded-xl ${
              item.active ? 'bg-white/10 border border-brand-peach/30' : 'bg-white/5'
            }`}
          >
            {item.completed ? (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: showItems[index] ? 1 : 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center"
              >
                <Check className="w-3.5 h-3.5 text-green-400" />
              </motion.div>
            ) : (
              <motion.div
                animate={{ x: [0, 4, 0] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="w-6 h-6 rounded-full bg-brand-peach/20 flex items-center justify-center"
              >
                <ChevronRight className="w-3.5 h-3.5 text-brand-peach" />
              </motion.div>
            )}
            <span className={`text-xs ${item.active ? 'text-white' : 'text-white/70'}`}>
              {item.label}
            </span>
          </motion.div>
        ))}
      </div>
      
      {/* Calendar Sync */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: showCalendar ? 1 : 0, y: showCalendar ? 0 : 10 }}
        className="mt-6 flex items-center gap-2"
      >
        <motion.div
          animate={{ 
            boxShadow: ['0 0 8px rgba(245,168,130,0.3)', '0 0 16px rgba(245,168,130,0.5)', '0 0 8px rgba(245,168,130,0.3)']
          }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-8 h-8 rounded-full bg-brand-peach/20 flex items-center justify-center"
        >
          <Calendar className="w-4 h-4 text-brand-peach" />
        </motion.div>
        <span className="text-white/60 text-[10px]">Synced to your calendar</span>
      </motion.div>
    </div>
  );
};

// Main Component with Swipe Animation
const NiaMockup = () => {
  const [currentScreen, setCurrentScreen] = useState(0);
  const screens = [NiaListeningScreen, EmotionalReadinessScreen, PathForwardScreen];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentScreen(prev => (prev + 1) % 3);
    }, 7000); // 7 seconds per screen for slower, more readable pacing
    
    return () => clearInterval(interval);
  }, []);
  
  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0,
    }),
  };
  
  return (
    <div className="relative w-full h-full flex items-center justify-center p-4">
      {/* Phone Frame */}
      <div className="relative w-[260px] h-[520px] bg-[#1a1a2e] rounded-[36px] shadow-2xl overflow-hidden border-4 border-gray-800">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-black rounded-b-xl z-20" />
        
        {/* Screen Content */}
        <div className="absolute inset-2 rounded-[28px] bg-gradient-to-b from-[#1a1a3e] to-[#0f0f23] overflow-hidden">
          {/* Subtle background elements */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-1/4 right-4 w-32 h-32 bg-brand-lavender/20 rounded-full blur-3xl" />
            <div className="absolute bottom-1/3 left-4 w-28 h-28 bg-brand-peach/15 rounded-full blur-3xl" />
          </div>
          
          {/* Circuit pattern */}
          <svg className="absolute inset-0 w-full h-full opacity-5" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path d="M10 10 L10 25 L25 25" stroke="#667eea" strokeWidth="0.3" fill="none" />
            <path d="M90 15 L75 15 L75 30" stroke="#667eea" strokeWidth="0.3" fill="none" />
            <path d="M15 85 L15 70 L30 70" stroke="#667eea" strokeWidth="0.3" fill="none" />
            <path d="M85 90 L85 75 L70 75" stroke="#667eea" strokeWidth="0.3" fill="none" />
            <circle cx="10" cy="10" r="1" fill="#667eea" />
            <circle cx="25" cy="25" r="0.8" fill="#667eea" />
            <circle cx="75" cy="15" r="1" fill="#667eea" />
            <circle cx="75" cy="30" r="0.8" fill="#667eea" />
          </svg>
          
          {/* Logo at top */}
          <div className="relative z-10 pt-10 flex flex-col items-center">
            <motion.div
              animate={{
                filter: ["drop-shadow(0 0 6px rgba(245,168,130,0.3))", "drop-shadow(0 0 12px rgba(245,168,130,0.5))", "drop-shadow(0 0 6px rgba(245,168,130,0.3))"]
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <img 
                src="/become-logo-transparent.png" 
                alt="Become" 
                className="h-12 w-auto object-contain"
              />
            </motion.div>
            <span className="text-[9px] text-white/40 tracking-wider mt-1">Reflect. Discover. Become.</span>
          </div>
          
          {/* Screen Content Area with Swipe Animation */}
          <div className="relative h-[320px] overflow-hidden">
            <AnimatePresence initial={false} custom={1} mode="wait">
              <motion.div
                key={currentScreen}
                custom={1}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="absolute inset-0"
              >
                {currentScreen === 0 && <NiaListeningScreen isActive={true} />}
                {currentScreen === 1 && <EmotionalReadinessScreen isActive={true} />}
                {currentScreen === 2 && <PathForwardScreen isActive={true} />}
              </motion.div>
            </AnimatePresence>
          </div>
          
          {/* Screen Indicator Dots */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {[0, 1, 2].map((index) => (
              <motion.div
                key={index}
                animate={{
                  scale: currentScreen === index ? 1.2 : 1,
                  backgroundColor: currentScreen === index ? '#f5a882' : 'rgba(255,255,255,0.3)',
                }}
                className="w-1.5 h-1.5 rounded-full"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export { NiaMockup };
